import { Panel, PanelLabel } from "@/components/PageHeader";
import { AccountActions, type AccountActionHandlers } from "@/components/accounts/AccountActions";
import { accountKindLabel, type Account } from "@/lib/finance";
import { money, moneyIn } from "@/lib/format";
import { FxBalance } from "@/components/accounts/FxBalance";
import { MsiCommitmentsNote } from "@/components/MsiCommitmentsNote";

function CardDetails({ a }: { a: Account }) {
  const pct = Math.round((a.meta.utilization_ratio ?? 0) * 100);
  const over = a.meta.over_limit_by ?? 0;
  return (
    <>
      {over > 0 && <p className="mt-2 text-sm font-semibold text-[#B23A1E]" data-testid={`card-over-limit-${a.id}`}>Tu tarjeta pasó de su límite por {money(over)}</p>}
      <div className="mt-3 h-2.5 overflow-hidden rounded-full bg-[#F4F5F7]">
        <div className={`h-full rounded-full transition-[width] duration-500 ${over > 0 ? "bg-[#B23A1E]" : "bg-[#1B2A4A]"}`} style={{ width: `${Math.min(pct, 100)}%` }} />
      </div>
      <div className="mt-2 flex flex-col gap-1 text-sm text-slate-500 sm:flex-row sm:justify-between">
        <span data-testid={`card-utilization-${a.id}`}>{a.meta.credit_limit ? `Usaste ${pct}% de tu límite de ${moneyIn(a.meta.credit_limit, a.currency)}` : "Sin límite capturado"}</span>
        <span>
          {a.meta.statement_day ? `Corte el día ${a.meta.statement_day}` : "Sin día de corte"}
          {a.meta.payment_day ? ` · Vence el día ${a.meta.payment_day}` : ""}
        </span>
      </div>
      <MsiCommitmentsNote c={a.meta.msi_commitments} testId={`card-msi-${a.id}`} text={(t) => `Compromisos en meses sin intereses: ${t} en los próximos meses`} />
      <p className="mt-2 text-sm text-slate-600" data-testid={`account-interest-${a.id}`}>Pagaste <b className="tabular text-[#1A1F2B]">{money(a.meta.interest_paid_year ?? 0)}</b> en intereses este año</p>
    </>
  );
}

const MONTHS = ["enero", "febrero", "marzo", "abril", "mayo", "junio", "julio", "agosto", "septiembre", "octubre", "noviembre", "diciembre"];

function nextPaymentLabel(day: number) {
  const now = new Date();
  const at = (y: number, m: number) => new Date(y, m, Math.min(day, new Date(y, m + 1, 0).getDate()));
  let d = at(now.getFullYear(), now.getMonth());
  if (d.getDate() < now.getDate()) d = at(now.getFullYear(), now.getMonth() + 1);
  return `${d.getDate()} de ${MONTHS[d.getMonth()]}`;
}

function LoanDetails({ a }: { a: Account }) {
  const day = a.meta.payment_day;
  return (
    <>
      <div className="mt-3 flex flex-col gap-1 text-sm text-slate-500 sm:flex-row sm:flex-wrap sm:gap-x-6">
        <span data-testid={`loan-monthly-${a.id}`}>Mensualidad: <b className="tabular text-[#1A1F2B]">{a.meta.loan_monthly_payment ? moneyIn(a.meta.loan_monthly_payment, a.currency) : "Sin capturar"}</b></span>
        <span data-testid={`loan-next-payment-${a.id}`}>Próximo pago: <b className="text-[#1A1F2B]">{a.current_balance >= 0 ? "Liquidado" : day ? nextPaymentLabel(day) : "Sin día de pago"}</b></span>
        {a.meta.linked_asset && <span data-testid={`loan-linked-asset-${a.id}`}>Ligado a: <b className="text-[#1A1F2B]">{a.meta.linked_asset.name}</b></span>}
      </div>
      <p className="mt-2 text-sm text-slate-600" data-testid={`account-interest-${a.id}`}>Pagaste <b className="tabular text-[#1A1F2B]">{money(a.meta.interest_paid_year ?? 0)}</b> en intereses este año</p>
    </>
  );
}

export function LiabilitiesCard({ accounts, total, ...handlers }: { accounts: Account[]; total: number } & AccountActionHandlers) {
  return (
    <Panel testId="liabilities-card">
      <div className="flex flex-col gap-1 sm:flex-row sm:items-center sm:justify-between">
        <PanelLabel>Lo que debes</PanelLabel>
        <p className="figure-lg text-[#1A1F2B]" data-testid="liabilities-total">{money(-total)}</p>
      </div>
      {accounts.length === 0 && <p className="py-6 text-sm text-slate-500" data-testid="liabilities-empty">No tienes créditos registrados.</p>}
      <ul className="divide-y divide-[#DDE1E8]">
        {accounts.map((a) => (
          <li key={a.id} className={`py-5 ${(a.meta.over_limit_by ?? 0) > 0 ? "-mx-3 rounded-xl border border-[#B23A1E]/40 bg-[#F8ECE8] px-3" : ""}`} data-testid={`liability-row-${a.id}`}>
            <div className="flex items-center justify-between gap-3">
              <div className="min-w-0">
                <p className="break-words font-semibold leading-snug text-[#1B2A4A]">{a.name}</p>
                <p className="text-xs text-slate-500" data-testid={`liability-subtype-${a.id}`}>{accountKindLabel(a)}</p>
              </div>
              <div className="flex items-center gap-2">
                <FxBalance a={a} />
                <AccountActions account={a} {...handlers} />
              </div>
            </div>
            {a.account_type === "loan" ? <LoanDetails a={a} /> : <CardDetails a={a} />}
            {a.notes && <p className="mt-2 whitespace-pre-line text-xs text-slate-500" data-testid={`account-notes-${a.id}`}>{a.notes}</p>}
          </li>
        ))}
      </ul>
    </Panel>
  );
}
